import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, UserPlus } from "lucide-react";
import { STATUS_ICON_SIZE } from "@/constants/ui";
import { useAuth } from "@/context/AuthContext";

const placementQuestions = [
  { id: "placement-1", type: "choice", prompt: "Complete:", question: "I ___ new here.", options: ["am", "is", "are"] },
  { id: "placement-2", type: "fill", prompt: "Escreva em inglês:", question: "Eu queria um café, por favor.", placeholder: "Can I get..." },
  { id: "placement-3", type: "choice", prompt: "Complete:", question: "They ___ at work today.", options: ["am", "is", "are"] },
  { id: "placement-4", type: "fill", prompt: "Escreva em inglês:", question: "Eu não entendo.", placeholder: "I don't..." },
  { id: "placement-5", type: "choice", prompt: "Escolha o passado:", question: "Yesterday, I ___ to the pharmacy.", options: ["go", "went", "gone"] },
  { id: "placement-6", type: "fill", prompt: "Escreva em inglês:", question: "Você pode repetir isso?", placeholder: "Could you..." },
  { id: "placement-7", type: "choice", prompt: "Escolha a frase natural:", question: "Eu vou ligar para você mais tarde.", options: ["I'll call you later", "I call later you", "I'm call you later"] },
  { id: "placement-8", type: "fill", prompt: "Escreva em inglês:", question: "Preciso de ajuda com meu compromisso.", placeholder: "I need help..." },
  { id: "placement-9", type: "choice", prompt: "Escolha o conector:", question: "I called ___ my internet is down.", options: ["because", "so", "although"] },
  { id: "placement-10", type: "fill", prompt: "Escreva em inglês:", question: "Se estiver disponível, eu fico com ele.", placeholder: "If it's available..." },
];

function RegisterField({ autoComplete, field, form, label, onChange, placeholder, required = false, type = "text" }) {
  return (
    <div>
      <label className="auth-label" htmlFor={`register-${field}-input`} data-testid={`register-${field}-label`}>{label}</label>
      <input id={`register-${field}-input`} className="auth-input" type={type} value={form[field]} onChange={(event) => onChange(field, event.target.value)} autoComplete={autoComplete} placeholder={placeholder} required={required} data-testid={`register-${field}-input`} />
    </div>
  );
}

function PlacementQuestion({ answer, index, onAnswer, question }) {
  return (
    <section className="placement-question-card" data-testid={`placement-question-card-${question.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="section-kicker" data-testid="placement-step-kicker">questão {index + 1}/10</p>
        <span className="placement-progress-label" data-testid="placement-progress-label">Teste obrigatório</span>
      </div>
      <p className="mt-6 text-sm font-black uppercase text-white/70" data-testid={`placement-question-prompt-${question.id}`}>{question.prompt}</p>
      <h2 className="mt-2 font-display text-3xl font-black text-white sm:text-4xl" data-testid={`placement-question-text-${question.id}`}>{question.question}</h2>
      {question.type === "choice" ? (
        <div className="mt-6 grid gap-3" data-testid={`placement-option-list-${question.id}`}>
          {question.options.map((option) => (
            <button key={option} type="button" className={`placement-option ${answer === option ? "placement-option-active" : ""}`} onClick={() => onAnswer(question.id, option)} data-testid={`placement-option-${question.id}-${option.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {option}
            </button>
          ))}
        </div>
      ) : (
        <input className="auth-input mt-6" value={answer || ""} onChange={(event) => onAnswer(question.id, event.target.value)} placeholder={question.placeholder} data-testid={`placement-answer-input-${question.id}`} />
      )}
    </section>
  );
}

function useRegistrationForm({ navigate, register }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", goals: "" });
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);
  const currentQuestion = placementQuestions[step];
  const profileComplete = form.name.trim() && form.email.trim() && form.password.trim().length >= 6;
  const currentAnswered = (answers[currentQuestion.id] || "").trim().length > 0;
  const allAnswered = placementQuestions.every((question) => (answers[question.id] || "").trim());
  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateAnswer = (questionId, value) => setAnswers((current) => ({ ...current, [questionId]: value }));
  const movePrevious = () => setStep((value) => Math.max(0, value - 1));
  const moveNext = () => setStep((value) => value + 1);
  const splitGoals = () => form.goals.split(",").map((goal) => goal.trim()).filter(Boolean);
  const buildPlacementAnswers = () => placementQuestions.map((question) => ({ question_id: question.id, answer: answers[question.id] }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!profileComplete || !allAnswered) {
      setError("Preencha seus dados e responda as 10 questões do teste de nível.");
      return;
    }
    setIsSubmitting(true);
    setError("");
    const result = await register({ ...form, goals: splitGoals(), placement_answers: buildPlacementAnswers() });
    setIsSubmitting(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setRegistered(true);
    navigate("/profile", { replace: true });
  };

  return { allAnswered, answers, currentAnswered, currentQuestion, error, form, handleSubmit, isSubmitting, moveNext, movePrevious, profileComplete, registered, step, updateAnswer, updateField };
}

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const registration = useRegistrationForm({ navigate, register });

  if (user && !registration.registered) return <Navigate to="/profile" replace />;

  return (
    <main className="auth-page" data-testid="register-page">
      <section className="auth-panel register-panel" data-testid="register-panel">
        <div>
          <p className="section-kicker" data-testid="register-page-kicker">teste de nível obrigatório</p>
          <h1 className="font-display text-4xl font-black sm:text-5xl" data-testid="register-page-heading">Crie sua conta. O sistema define seu nível.</h1>
          <p className="mt-3 text-sm font-bold text-white/70" data-testid="register-page-description">Você responde 10 questões misturando múltipla escolha e escrita. Não precisa escolher nível manualmente.</p>
        </div>
        <form className="auth-form" onSubmit={registration.handleSubmit} data-testid="register-form">
          <div className="register-grid">
            <RegisterField autoComplete="name" field="name" form={registration.form} label="Nome" onChange={registration.updateField} required />
            <RegisterField autoComplete="email" field="email" form={registration.form} label="Email" onChange={registration.updateField} required type="email" />
            <RegisterField autoComplete="new-password" field="password" form={registration.form} label="Senha" onChange={registration.updateField} required type="password" />
            <RegisterField autoComplete="tel" field="phone" form={registration.form} label="Telefone" onChange={registration.updateField} />
            <div className="register-wide">
              <RegisterField field="goals" form={registration.form} label="Objetivos" onChange={registration.updateField} placeholder="trabalho, médico, aluguel" />
            </div>
          </div>
          <div className="placement-progress-track" data-testid="placement-progress-track"><span style={{ width: `${((registration.step + 1) / placementQuestions.length) * 100}%` }} data-testid="placement-progress-fill" /></div>
          <PlacementQuestion answer={registration.answers[registration.currentQuestion.id]} index={registration.step} onAnswer={registration.updateAnswer} question={registration.currentQuestion} />
          <div className="flex flex-wrap justify-between gap-3" data-testid="placement-navigation-controls">
            <button type="button" className="secondary-action w-auto" onClick={registration.movePrevious} disabled={registration.step === 0} data-testid="placement-previous-button"><ArrowLeft size={18} /> Voltar</button>
            {registration.step < placementQuestions.length - 1 ? (
              <button type="button" className="primary-action w-auto" onClick={registration.moveNext} disabled={!registration.currentAnswered} data-testid="placement-next-button">Próxima <ArrowRight size={18} /></button>
            ) : (
              <button className="primary-action w-auto" type="submit" disabled={registration.isSubmitting || !registration.profileComplete || !registration.allAnswered} data-testid="register-submit-button">
                {registration.isSubmitting ? "Criando..." : "Criar conta"} <UserPlus size={18} />
              </button>
            )}
          </div>
          {registration.allAnswered && <div className="grammar-score-panel" data-testid="placement-ready-panel"><CheckCircle2 size={STATUS_ICON_SIZE} /> Teste completo. Seu nível será calculado automaticamente ao criar a conta.</div>}
          {registration.error && <div className="auth-error" role="alert" data-testid="register-error-alert">{registration.error}</div>}
        </form>
        <p className="auth-switch" data-testid="register-login-prompt">
          Já tem conta? <Link to="/login" data-testid="register-login-link">Entrar</Link>
        </p>
      </section>
    </main>
  );
}